import { formatNumber, useLocale } from "@ncp/i18n";
import { cn } from "@ncp/ui";

/**
 * A chart a written assessment places with a fenced ```chart block holding JSON. Four shapes, each drawn in the platform's palette:
 * - `bars`: one bar per row (one or two series), with an optional marker line ("10% concern mark");
 * - `shares`: before and after as 100% stacked bars, one segment per firm;
 * - `line`: one or more series over an x axis, with an optional marker line (a sensitivity reading);
 * - `timeline`: dated milestones, each done, current or next.
 * A block that does not parse is shown as nothing rather than as raw JSON.
 */
export type AssessmentChartSpec =
  | { type: "bars"; title?: string; unit?: string; max?: number; series?: string[]; rows: { label: string; values: number[]; tone?: Tone }[]; marker?: { value: number; label: string } }
  | { type: "shares"; title?: string; rows: { label: string; parts: { name: string; value: number; tone?: Tone }[] }[] }
  | { type: "line"; title?: string; unit?: string; x: (string | number)[]; xLabel?: string; series: { name: string; values: number[]; tone?: Tone }[]; marker?: { value: number; label: string } }
  | { type: "timeline"; title?: string; items: { date: string; label: string; status?: "done" | "current" | "next" }[] };

type Tone = "sea" | "gold" | "sand" | "green" | "amber" | "red" | "slate";

const FILL: Record<Tone, string> = { sea: "bg-seablue-600", gold: "bg-aegold-600", sand: "bg-sand-400", green: "bg-success-500", amber: "bg-warning-500", red: "bg-danger-500", slate: "bg-aeblack-500" };
const STROKE: Record<Tone, string> = { sea: "stroke-seablue-600", gold: "stroke-aegold-600", sand: "stroke-sand-500", green: "stroke-success-600", amber: "stroke-warning-500", red: "stroke-danger-500", slate: "stroke-aeblack-500" };
const DOT: Record<Tone, string> = { sea: "fill-seablue-600", gold: "fill-aegold-600", sand: "fill-sand-500", green: "fill-success-600", amber: "fill-warning-500", red: "fill-danger-500", slate: "fill-aeblack-500" };
const SERIES: Tone[] = ["sea", "gold", "slate", "sand"];
const PARTS: Tone[] = ["gold", "sea", "slate", "sand", "amber"];

export function parseChart(source: string): AssessmentChartSpec | null {
  try {
    const spec = JSON.parse(source) as AssessmentChartSpec;
    return spec && typeof spec === "object" && ["bars", "shares", "line", "timeline"].includes(spec.type) ? spec : null;
  } catch {
    return null;
  }
}

export function AssessmentChart({ spec }: { spec: AssessmentChartSpec }) {
  return (
    <figure className="not-prose my-3 rounded-lg border border-sand-200 bg-white px-4 py-3">
      {spec.title ? <figcaption className="mb-2.5 text-xs font-semibold text-aeblack-900">{spec.title}</figcaption> : null}
      {spec.type === "bars" ? <Bars spec={spec} /> : spec.type === "shares" ? <Shares spec={spec} /> : spec.type === "line" ? <Line spec={spec} /> : <Timeline spec={spec} />}
    </figure>
  );
}

function useFmt(unit?: string) {
  const { locale } = useLocale();
  return (v: number) => `${formatNumber(v, locale)}${unit ?? ""}`;
}

function Legend({ items }: { items: { name: string; tone: Tone }[] }) {
  return (
    <ul className="mt-2.5 flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-sand-700">
      {items.map((s) => <li key={s.name} className="flex items-center gap-1.5"><span className={cn("size-2 rounded-sm", FILL[s.tone])} aria-hidden />{s.name}</li>)}
    </ul>
  );
}

function Bars({ spec }: { spec: Extract<AssessmentChartSpec, { type: "bars" }> }) {
  const fmt = useFmt(spec.unit);
  const max = spec.max ?? Math.max(...spec.rows.flatMap((r) => r.values), spec.marker?.value ?? 0) * 1.1;
  const pct = (v: number) => `${Math.max(0, Math.min(100, (v / (max || 1)) * 100))}%`;
  return (
    <>
      <div className="relative">
        <ul className="space-y-2">
          {spec.rows.map((r) => (
            <li key={r.label} className="grid grid-cols-[minmax(0,9rem)_1fr] items-center gap-3 text-xs">
              <span className="truncate text-sand-800" title={r.label}>{r.label}</span>
              <span className="space-y-1">
                {r.values.map((v, i) => (
                  <span key={i} className="flex items-center gap-2">
                    <span className="relative h-2.5 flex-1 rounded-sm bg-sand-100">
                      <span className={cn("absolute inset-y-0 start-0 rounded-sm", FILL[r.tone && r.values.length === 1 ? r.tone : SERIES[i % SERIES.length]])} style={{ width: pct(v) }} />
                      {spec.marker ? <span className="absolute inset-y-[-3px] w-px bg-danger-500" style={{ insetInlineStart: pct(spec.marker.value) }} aria-hidden /> : null}
                    </span>
                    <span className="w-14 shrink-0 text-end font-medium tabular-nums text-aeblack-900">{fmt(v)}</span>
                  </span>
                ))}
              </span>
            </li>
          ))}
        </ul>
      </div>
      {spec.series?.length || spec.marker ? (
        <ul className="mt-2.5 flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-sand-700">
          {(spec.series ?? []).map((s, i) => <li key={s} className="flex items-center gap-1.5"><span className={cn("size-2 rounded-sm", FILL[SERIES[i % SERIES.length]])} aria-hidden />{s}</li>)}
          {spec.marker ? <li className="flex items-center gap-1.5"><span className="h-3 w-px bg-danger-500" aria-hidden />{spec.marker.label}</li> : null}
        </ul>
      ) : null}
    </>
  );
}

function Shares({ spec }: { spec: Extract<AssessmentChartSpec, { type: "shares" }> }) {
  const names = [...new Set(spec.rows.flatMap((r) => r.parts.map((p) => p.name)))];
  const toneOf = (name: string, explicit?: Tone) => explicit ?? PARTS[names.indexOf(name) % PARTS.length];
  const tones = new Map<string, Tone>();
  spec.rows.forEach((r) => r.parts.forEach((p) => tones.set(p.name, toneOf(p.name, p.tone))));
  return (
    <>
      <ul className="space-y-2.5">
        {spec.rows.map((r) => {
          const total = r.parts.reduce((s, p) => s + p.value, 0) || 1;
          return (
            <li key={r.label} className="grid grid-cols-[4.5rem_1fr] items-center gap-3 text-xs">
              <span className="text-sand-800">{r.label}</span>
              <span className="flex h-6 overflow-hidden rounded-md">
                {r.parts.map((p) => (
                  <span key={p.name} title={`${p.name}: ${p.value}%`} className={cn("flex min-w-0 items-center justify-center border-e border-white/70 text-[10px] font-semibold text-white last:border-e-0", FILL[tones.get(p.name)!])} style={{ width: `${(p.value / total) * 100}%` }}>
                    {p.value >= 9 ? `${p.value}%` : ""}
                  </span>
                ))}
              </span>
            </li>
          );
        })}
      </ul>
      <Legend items={names.map((n) => ({ name: n, tone: tones.get(n)! }))} />
    </>
  );
}

function Line({ spec }: { spec: Extract<AssessmentChartSpec, { type: "line" }> }) {
  const fmt = useFmt(spec.unit);
  const W = 520, H = 170, L = 40, R = 12, T = 10, B = 26;
  const values = [...spec.series.flatMap((s) => s.values), spec.marker?.value ?? 0];
  const raw = Math.max(...values) * 1.12 || 1;
  // A round top for the axis: 5, 10, 25, 50, 100… so the ticks read as whole numbers.
  const step = [1, 2, 2.5, 5, 10].map((m) => m * 10 ** Math.floor(Math.log10(raw))).find((s) => s * 2 >= raw / 2) ?? raw;
  const top = Math.ceil(raw / step) * step;
  const x = (i: number) => L + (spec.x.length <= 1 ? 0 : (i / (spec.x.length - 1)) * (W - L - R));
  const y = (v: number) => T + (1 - v / top) * (H - T - B);
  const ticks = [0, top / 2, top].map((v) => (top >= 10 ? Math.round(v) : Math.round(v * 10) / 10));
  return (
    <>
      <svg viewBox={`0 0 ${W} ${H}`} className="h-auto w-full" role="img" aria-label={spec.title}>
        {ticks.map((v) => (
          <g key={v}>
            <line x1={L} x2={W - R} y1={y(v)} y2={y(v)} className="stroke-sand-200" strokeWidth={1} />
            <text x={L - 6} y={y(v) + 3} textAnchor="end" className="fill-sand-600 text-[10px]">{fmt(v)}</text>
          </g>
        ))}
        {spec.marker ? (
          <g>
            <line x1={L} x2={W - R} y1={y(spec.marker.value)} y2={y(spec.marker.value)} className="stroke-danger-500" strokeDasharray="4 3" strokeWidth={1.25} />
            <text x={W - R} y={y(spec.marker.value) - 4} textAnchor="end" className="fill-danger-600 text-[10px] font-medium">{spec.marker.label}</text>
          </g>
        ) : null}
        {spec.series.map((s, si) => {
          const tone = s.tone ?? SERIES[si % SERIES.length];
          return (
            <g key={s.name}>
              <polyline points={s.values.map((v, i) => `${x(i)},${y(v)}`).join(" ")} fill="none" className={STROKE[tone]} strokeWidth={2} strokeLinejoin="round" />
              {s.values.map((v, i) => <circle key={i} cx={x(i)} cy={y(v)} r={3} className={DOT[tone]}><title>{`${s.name}: ${fmt(v)} at ${spec.x[i]}`}</title></circle>)}
            </g>
          );
        })}
        {spec.x.map((label, i) => <text key={i} x={x(i)} y={H - 8} textAnchor="middle" className="fill-sand-600 text-[10px]">{label}</text>)}
      </svg>
      <Legend items={spec.series.map((s, i) => ({ name: s.name, tone: s.tone ?? SERIES[i % SERIES.length] }))} />
      {spec.xLabel ? <p className="mt-1 text-[11px] text-sand-600">{spec.xLabel}</p> : null}
    </>
  );
}

function Timeline({ spec }: { spec: Extract<AssessmentChartSpec, { type: "timeline" }> }) {
  return (
    <ol className="relative ms-1.5 space-y-2.5 border-s border-sand-200 ps-4">
      {spec.items.map((it, i) => (
        <li key={i} className="relative text-xs">
          <span className={cn("absolute -start-[21px] top-0.5 size-2.5 rounded-full ring-2 ring-white", it.status === "done" ? "bg-success-500" : it.status === "current" ? "bg-aegold-600" : "bg-sand-300")} aria-hidden />
          <span className="me-2 font-medium tabular-nums text-aeblack-900">{it.date}</span>
          <span className={cn(it.status === "next" || !it.status ? "text-sand-700" : "text-aeblack-900")}>{it.label}</span>
        </li>
      ))}
    </ol>
  );
}
